import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box, Button, Heading, HStack, IconButton, SimpleGrid, Spinner, Text, VStack,
} from '@chakra-ui/react'
import { FiChevronLeft, FiChevronRight, FiRefreshCw, FiSave } from 'react-icons/fi'
import { toaster } from '@/components/ui/toaster'
import { StickyActionFooter } from '../../shared'
import { extractApiMessage } from '../../../lib/apiError'
import { PacketPreviewDrawer } from '../PacketPreviewDrawer'
import { DraftRecoveryBanner } from '../scheduler/DraftRecoveryBanner'
import { ScheduleConflictBanner } from '../scheduler/ScheduleConflictBanner'
import { MeetingDetailSheet } from '../scheduler/MeetingDetailSheet'
import { AttendeePickerSheet } from '../scheduler/AttendeePickerSheet'
import { useLobbyScheduleEditor } from '../../../hooks/useLobbyScheduleEditor'
import { useDayOfLeads } from '../../../hooks/useLobbyDay'
import { useNow } from '../../../hooks/useNow'
import { useIsMobile } from '../../../hooks/useIsMobile'
import { useAuth } from '../../../hooks/useAuth'
import type { ScheduleEditorState } from '../../../pages/LobbyDayEventPage'
import type { DayOfLeadDto } from '../../../types/lobbyDay'
import { DayOfGroupCard } from './DayOfGroupCard'
import { ReportBackChecklist } from './ReportBackChecklist'
import { RecordReportSheet } from './RecordReportSheet'
import { boardDate, buildBoard, type BoardCard } from './dayOfBoardModel'

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T00:00:00')
  d.setDate(d.getDate() + days)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
}

const SECTIONS = [
  { key: 'now', title: 'Happening now' },
  { key: 'next', title: 'Up next' },
  { key: 'owed', title: 'Report-back owed' },
  { key: 'done', title: 'Done' },
  { key: 'cancelled', title: 'Cancelled' },
] as const

interface DayOfBoardProps {
  eventId: string
  onEditorState: (s: ScheduleEditorState) => void
}

/** The organizer's day-of board (Lobby Day). Mounts its OWN schedule editor (DB-13 — the event
 *  page's tabs are lazyMount + unmountOnExit, so it never coexists with the Schedule tab's), so
 *  room / status / meeting-with / ask / move edits stage and save exactly like the scheduler. */
export function DayOfBoard({ eventId, onEditorState }: DayOfBoardProps) {
  const editor = useLobbyScheduleEditor(eventId)
  const {
    schedule, legislators, attendees, asks, meetings, startDate, endDate, isLoading, error,
    isDirty, isSaving, save, discardDraft, restoreDraft, recoverableDraft, conflict, reloadLatest, refresh,
    assign, unassign, move, deleteMeeting, reschedule, setMeetingAsk, setMeetingRoom, setMeetingStatus,
    setAttendance, setMeetingWithWhom, setRole, setMyStaffTag, recordOutcome,
  } = editor
  const { data: leads } = useDayOfLeads(eventId)
  const now = useNow()
  const isMobile = useIsMobile()
  const { user } = useAuth()

  // Same upward contract as MobileScheduleTab: the event page's unsaved-changes guard reads it.
  useEffect(() => {
    onEditorState({ isDirty, isSaving, save, discardDraft })
  }, [isDirty, isSaving, save, discardDraft, onEditorState])
  useEffect(() => () => onEditorState({ isDirty: false, isSaving: false, save: () => {}, discardDraft: () => {} }), [onEditorState])
  // DB-15: the board refetches on mount (cached schedule/leads may be minutes old — another
  // organizer's no-shows, leads, staff tags or attendee report-backs). With nothing cached the
  // first load IS that fetch. With a cache, the editor reads dirty until it hydrates from it (and
  // `refresh` is a no-op while dirty), so the one-shot refresh waits for the clean hydrated state.
  const mountRefreshDone = useRef(!schedule)
  useEffect(() => {
    if (mountRefreshDone.current || isDirty || !schedule) return
    mountRefreshDone.current = true
    refresh()
  }, [refresh, isDirty, schedule])

  const [pickedDate, setPickedDate] = useState<string | null>(null)
  const [openMeetingId, setOpenMeetingId] = useState<string | null>(null)
  const [packetMeetingId, setPacketMeetingId] = useState<string | null>(null)
  const [recordMeetingId, setRecordMeetingId] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)

  // DB-18: today when it falls within the event, else the start date — until the user switches day.
  const date = pickedDate ?? (startDate ? boardDate(startDate, endDate, now) : '')
  const showDaySwitcher = !!endDate && !!startDate && endDate !== startDate

  const board = useMemo(() => buildBoard(meetings, date, now), [meetings, date, now])
  const legislatorById = useMemo(
    () => new Map(legislators.map((l) => [l.legislatorId, l] as const)), [legislators])
  const leadsByAssignment = useMemo(
    () => new Map((leads ?? []).map((l) => [l.assignmentId, l] as const)), [leads])

  const openMeeting = openMeetingId ? meetings.find((m) => m.id === openMeetingId) ?? null : null
  const openLegislator = openMeeting ? legislatorById.get(openMeeting.legislatorId) : undefined
  const assignedIds = useMemo(
    () => new Set(openMeeting?.assignments.map((a) => a.attendeeId) ?? []), [openMeeting])
  const recordMeeting = recordMeetingId ? meetings.find((m) => m.id === recordMeetingId) ?? null : null

  if (isLoading) return <VStack py={12}><Spinner size="lg" color="accent" /></VStack>
  if (!!error) return <Text color="danger.fg">{extractApiMessage(error, 'Failed to load schedule.')}</Text>
  if (!schedule) return <Text color="danger.fg">Failed to load schedule.</Text>

  const saveChanges = () => save({
    onError: () => { toaster.create({ title: 'Failed to save schedule', type: 'error', duration: 3000 }) },
  })
  const leadContactFor = (c: BoardCard): DayOfLeadDto | undefined =>
    c.lead ? leadsByAssignment.get(c.lead.assignmentId) : undefined
  const isEmpty = SECTIONS.every((s) => board[s.key].length === 0)

  return (
    <Box pb={isMobile && isDirty ? '96px' : undefined}>
      <HStack mb={4} gap={2} justify="space-between" flexWrap="wrap">
        {showDaySwitcher ? (
          <HStack gap={2}>
            <IconButton
              aria-label="Previous day"
              size="sm"
              variant="outline"
              minW="44px"
              h="44px"
              disabled={date <= (startDate ?? '')}
              onClick={() => setPickedDate(addDays(date, -1))}><FiChevronLeft /></IconButton>
            <Text fontWeight="medium" minW="130px" textAlign="center">{formatDateLabel(date)}</Text>
            <IconButton
              aria-label="Next day"
              size="sm"
              variant="outline"
              minW="44px"
              h="44px"
              disabled={date >= (endDate ?? '')}
              onClick={() => setPickedDate(addDays(date, 1))}><FiChevronRight /></IconButton>
          </HStack>
        ) : <Text fontWeight="medium">{formatDateLabel(date)}</Text>}
        <HStack gap={2} flexWrap="wrap" justify="flex-end">
          {isDirty && <Text fontSize="xs" color="fg.muted">Save or discard your edits first</Text>}
          <Button
            size="sm"
            variant="outline"
            minH="44px"
            disabled={isDirty}
            title={isDirty ? 'Save or discard your edits first' : undefined}
            onClick={refresh}><FiRefreshCw />Refresh</Button>
          {!isMobile && isDirty && (
            <Button
              size="sm"
              colorPalette="brand"
              minH="44px"
              disabled={conflict}
              loading={isSaving}
              onClick={saveChanges}><FiSave />Save changes</Button>
          )}
        </HStack>
      </HStack>

      {conflict && <ScheduleConflictBanner onReload={reloadLatest} />}
      {recoverableDraft && <DraftRecoveryBanner onRestore={restoreDraft} onDiscard={discardDraft} />}

      {isEmpty ? (
        <Text color="fg.muted" fontSize="sm" textAlign="center" py={8}>No meetings on this day.</Text>
      ) : (
        <>
          <ReportBackChecklist
            owed={board.owed}
            leadsByAssignment={leadsByAssignment}
            legislatorById={legislatorById}
            onRecord={setRecordMeetingId}
          />
          <VStack align="stretch" gap={6}>
            {SECTIONS.filter((s) => board[s.key].length > 0).map((s) => (
              <Box as="section" key={s.key} aria-label={s.title}>
                <Heading size="sm" mb={2}>
                  {s.title} <Text as="span" color="fg.muted" fontWeight="normal">({board[s.key].length})</Text>
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap={3}>
                  {board[s.key].map((c) => (
                    <DayOfGroupCard
                      key={c.meeting.id}
                      card={c}
                      legislator={legislatorById.get(c.meeting.legislatorId)}
                      leadContact={leadContactFor(c)}
                      currentUserId={user?.id}
                      onOpen={setOpenMeetingId}
                      onPacket={setPacketMeetingId}
                      onRecord={setRecordMeetingId}
                    />
                  ))}
                </SimpleGrid>
              </Box>
            ))}
          </VStack>
        </>
      )}

      <MeetingDetailSheet
        isOpen={openMeeting !== null}
        onClose={() => setOpenMeetingId(null)}
        meeting={openMeeting}
        legislator={openLegislator}
        allMeetings={meetings}
        legislatorById={legislatorById}
        onUnassign={(assignmentId) => { if (openMeeting) unassign(openMeeting.id, assignmentId) }}
        onMove={(assignmentId, toMeetingId, attendeeId) => { if (openMeeting) move(assignmentId, openMeeting.id, toMeetingId, attendeeId) }}
        onAddAttendee={() => setPickerOpen(true)}
        onDelete={(meetingId) => { deleteMeeting(meetingId); setOpenMeetingId(null) }}
        onReschedule={reschedule}
        onOpenPacket={(meetingId) => { setPacketMeetingId(meetingId); setOpenMeetingId(null) }}
        asks={asks}
        onSetAsk={(meetingId, askId) => setMeetingAsk(meetingId, askId)}
        onSetRoom={(meetingId, room) => setMeetingRoom(meetingId, room)}
        onSetStatus={(meetingId, status) => setMeetingStatus(meetingId, status)}
        onSetAttendance={(meetingId, assignmentId, status) => setAttendance(meetingId, assignmentId, status)}
        onSetWithWhom={setMeetingWithWhom}
        onSetRole={setRole}
        currentUserId={user?.id}
        onSetMyStaffTag={setMyStaffTag}
      />

      <AttendeePickerSheet
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        attendees={attendees}
        legislator={openLegislator}
        assignedAttendeeIds={assignedIds}
        onSelect={(attendeeId) => { if (openMeeting) assign(openMeeting.id, attendeeId) }}
      />

      <PacketPreviewDrawer eventId={eventId} meetingId={packetMeetingId} onClose={() => setPacketMeetingId(null)} />

      <RecordReportSheet
        key={recordMeeting?.id ?? 'none'}
        meeting={recordMeeting}
        isOpen={recordMeeting !== null}
        onClose={() => setRecordMeetingId(null)}
        onSave={(req, opts) => { if (recordMeeting) recordOutcome(recordMeeting.id, req, opts) }}
      />

      {isDirty && (
        <StickyActionFooter>
          <Button
            colorPalette="brand"
            minH="44px"
            disabled={conflict}
            loading={isSaving}
            onClick={saveChanges}><FiSave />Save changes</Button>
        </StickyActionFooter>
      )}
    </Box>
  )
}
